import { t, localizedName } from "../utils/i18n.ts";
import { COLORS } from "../core/constants.ts";
import type { EventBus } from "../core/EventBus.ts";
import type { PayloadRack } from "../systems/PayloadRack.ts";
import {
  applyCardEffect,
  getPayloadTargetMode,
} from "../game/cardEffectHandlers.ts";
import type { CardEffectHandlerDeps } from "../game/handlers/types.ts";
import type { RuntimeState } from "../game/types.ts";
import { setPayloadTargeting } from "../game/stateIntents.ts";
import { PANEL } from "../rendering/designTokens.ts";
import {
  UI_FONT_STACK,
  UI_TYPE,
  UI_SPACE,
  UI_EASE,
  UI_DURATION,
  roleFontSize,
  withUiScale,
} from "./uiTokens.ts";

import { RARITY_SLOT_COLORS } from "../core/palette.ts";
import { InfoTooltip } from "./InfoTooltip.ts";
import type { Card } from "../entities/types.ts";

/**
 * BL-292 — give a payload name break opportunities at its SEPARATORS.
 *
 * Real payload names are long single tokens joined by punctuation, not prose:
 * `FIREWALL_VERSTÄRKUNG` (20 chars), `ÜBERLAST::MALWARE`, `PANZERBRECHER.dll`.
 * In the two-up rack a cell is ~157 design px, so such a token cannot fit on one
 * line — and with no break opportunity in it, CSS either overflows the chip or
 * (with `overflow-wrap: anywhere`) guillotines it mid-word: `ÜBERLADUNGSPROT` /
 * `OKOLL`. Both were visible on device.
 *
 * A `<wbr>` after each `_`, `::`, `.`, `-` or `/` turns the token's own
 * structure into the wrap points, so it breaks where a reader would break it.
 * `overflow-wrap: break-word` then only ever fires if a single SEGMENT still
 * does not fit, which is the honest fallback rather than the default behaviour.
 *
 * Built as DOM nodes, never as an HTML string: the text is data/i18n content and
 * this keeps it text, so a name can never inject markup.
 */
export function appendBreakableName(host: HTMLElement, name: string): void {
  // Split AFTER each separator run, keeping it with the segment before it.
  const parts = name.split(/(?<=[_.\-/]|::)/);
  parts.forEach((part, i) => {
    host.appendChild(document.createTextNode(part));
    if (i < parts.length - 1) host.appendChild(document.createElement("wbr"));
  });
}

/**
 * BL-290 — the chip's slab height, in design px. Declared once because two
 * rules need it: the base `.payload-chip` floor and the `pointer:coarse`
 * `max(44px, …)` guard below it. When those two drifted apart the coarse rule
 * silently became a CAP — see the BL-291 note at the media query.
 */
const CHIP_MIN_H = 52;

/**
 * PayloadRackBar — shows armed payload chips in the right HUD panel.
 * Click a chip to deploy (fire) the payload.
 */
export class PayloadRackBar {
  private container: HTMLDivElement;
  private chipsContainer: HTMLDivElement;
  private rack: PayloadRack;
  private bus: EventBus;
  private effectCtx: CardEffectHandlerDeps | null = null;
  private gs: RuntimeState | null = null;
  private unsubs: (() => void)[] = [];
  private hot = false;
  private timeWarpAvailable = false;
  private readonly tooltip = new InfoTooltip();

  constructor(parent: HTMLElement, rack: PayloadRack, bus: EventBus) {
    this.rack = rack;
    this.bus = bus;
    this.container = document.createElement("div");
    this.container.id = "payload-rack-bar";
    this.injectStyles();

    const header = document.createElement("div");
    header.className = "payload-header";
    header.textContent = t("payload.header");
    this.container.appendChild(header);

    this.chipsContainer = document.createElement("div");
    this.chipsContainer.className = "payload-chips";
    this.container.appendChild(this.chipsContainer);

    parent.appendChild(this.container);

    this.unsubs.push(
      bus.on("payload.armed", () => this.render()),
      bus.on("payload.fired", () => this.render()),
      bus.on("payload.targetingStarted", () => this.render()),
      bus.on("payload.targetingCancelled", () => this.render()),
    );
  }

  /** Provide the effect context for deploying payloads. */
  setEffectContext(ctx: CardEffectHandlerDeps, gs?: RuntimeState): void {
    this.effectCtx = ctx;
    if (gs) this.gs = gs;
  }

  /**
   * Toggle deployable state (live enemies present). Drives the chip call-to-
   * action ("\u25B8 DEPLOY") + the strong pulse, and gates deployment so a payload
   * can't be wasted into an empty grid.
   */
  setHot(hot: boolean): void {
    if (hot === this.hot) return;
    this.hot = hot;
    this.container.classList.toggle("payload-rack--hot", hot);
    this.render();
  }

  setTimeWarpAvailable(available: boolean): void {
    if (available === this.timeWarpAvailable) return;
    this.timeWarpAvailable = available;
    this.render();
  }

  private render(): void {
    this.tooltip.hide(); // CR-1: never orphan a tooltip across a re-render
    this.chipsContainer.innerHTML = "";
    for (let i = 0; i < this.rack.count; i++) {
      const card = this.rack.payloads[i]!;
      const chip = document.createElement("button");
      chip.className = "payload-chip";
      chip.type = "button";
      const color = RARITY_SLOT_COLORS[card.rarity] ?? "#FFD700";
      chip.style.setProperty("--chip-color", color);
      const targetMode = getPayloadTargetMode(card);
      const isAimed = targetMode && targetMode !== "global";
      const isCurrentlyAiming = this.gs?.payloadTargetingCard === card;
      // State label: actively aiming → "◎ AIMING"; hot + aimed → "◎ AIM";
      // hot + global → "▸ DEPLOY"; not hot → "ARMED"
      let state: string;
      if (isCurrentlyAiming) {
        state = "◎ AIMING";
        chip.classList.add("payload-chip--aiming");
      } else if (this.hot && isAimed) {
        state = `◎ ${t("payload.deploy")}`;
      } else if (this.hot) {
        state = `\u25B8 ${t("payload.deploy")}`;
      } else {
        state = t("payload.armed");
      }
      chip.innerHTML = `<span class="payload-chip-glyph">\u25BC</span><span class="payload-chip-name"></span><span class="payload-chip-state">${state}</span>`;
      appendBreakableName(
        chip.querySelector(".payload-chip-name") as HTMLElement,
        localizedName("card", card.id, card.name),
      );
      chip.disabled = !this.hot;
      // CR-1: the shared card-style tooltip (with the HOW-to-deploy line),
      // replacing the bare native title. Flips LEFT of the far-right rack.
      chip.addEventListener("mouseenter", () =>
        this.tooltip.show(chip, this.payloadModel(card, targetMode, color), "left"),
      );
      chip.addEventListener("mouseleave", () => this.tooltip.hide());
      chip.addEventListener("click", () => this.deployAt(i));
      this.chipsContainer.appendChild(chip);
    }

    if (this.timeWarpAvailable) {
      const twChip = document.createElement("button");
      twChip.className = "payload-chip payload-chip--timewarp";
      twChip.type = "button";
      twChip.style.setProperty("--chip-color", COLORS.ICE_CYAN);
      const twState = this.hot
        ? `\u25B8 ${t("payload.deploy")}`
        : t("payload.armed");
      twChip.innerHTML = `<span class="payload-chip-glyph">[T]</span><span class="payload-chip-name"></span><span class="payload-chip-state">${twState}</span>`;
      appendBreakableName(
        twChip.querySelector(".payload-chip-name") as HTMLElement,
        t("payload.timewarp"),
      );
      twChip.disabled = !this.hot;
      twChip.addEventListener("mouseenter", () =>
        this.tooltip.show(
          twChip,
          {
            name: t("payload.timewarp"),
            accent: COLORS.ICE_CYAN,
            tag: "PAYLOAD · AUTO",
            impact: t("payload.timewarp.impact"),
            how: t("payload.how.timewarp"),
          },
          "left",
        ),
      );
      twChip.addEventListener("mouseleave", () => this.tooltip.hide());
      twChip.addEventListener("click", () => {
        this.bus.emit("game.activateTimeWarp");
      });
      this.chipsContainer.appendChild(twChip);
    }
  }

  /** CR-1: build the shared-tooltip model for a payload card, incl. the HOW
   *  (auto vs ◎aim-enemy vs ◎aim-cell) line derived from its target mode. */
  private payloadModel(
    card: Card,
    targetMode: ReturnType<typeof getPayloadTargetMode>,
    accent: string,
  ): import("./InfoTooltip.ts").InfoTooltipModel {
    const name = localizedName("card", card.id, card.name);
    const desc = localizedName("card", `${card.id}.desc`, card.description ?? "");
    const headline = card.headline
      ? localizedName("card", `${card.id}.headline`, card.headline)
      : card.headline;
    const mode = targetMode ?? "global";
    const tag = mode === "global" ? "PAYLOAD · AUTO" : "PAYLOAD · ◎AIM";
    return {
      name,
      accent,
      tag,
      impact: headline,
      body: desc ? [desc] : [],
      how: t(`payload.how.${mode}`),
      meta: `[${card.rarity}]`,
    };
  }

  private deployAt(index: number): void {
    if (!this.hot) return; // only deployable while there are live targets
    const card = this.rack.payloads[index];
    if (!card || !this.effectCtx) return;

    // BL-70: targeted payloads enter aim mode instead of firing immediately.
    const targetMode = getPayloadTargetMode(card);
    if (targetMode && targetMode !== "global" && this.gs) {
      setPayloadTargeting(this.gs, targetMode, card);
      this.bus.emit("payload.targetingStarted", { card });
      return;
    }

    // Global payloads fire immediately (legacy path).
    const deployed = this.rack.deploy(index);
    if (!deployed) return;
    applyCardEffect(deployed, this.effectCtx);
  }

  destroy(): void {
    for (const unsub of this.unsubs) unsub();
    this.unsubs = [];
    this.tooltip.hide();
    this.container.remove();
  }

  private injectStyles(): void {
    if (document.getElementById("payload-rack-styles")) return;
    const style = document.createElement("style");
    style.id = "payload-rack-styles";
    const T = UI_TYPE;
    style.textContent = `
      /* D-335 — tightened xxs(4px)->hair(2px) on both the header-to-chips gap
         and the container's own top/bottom padding: real headroom against the
         restored slab rack's own vertical budget (docs/decisions.md D-335),
         the same "hair" tightening D-333 used on the loadout slots. */
      #payload-rack-bar {
        display: flex;
        flex-direction: column;
        gap: ${UI_SPACE.hair}px;
        padding: ${UI_SPACE.hair}px 0;
        font-family: ${UI_FONT_STACK};
      }
      .payload-header {
        font-size: ${roleFontSize("micro")};
        color: #FFD700;
        font-family: ${UI_FONT_STACK};
        letter-spacing: 1px;
        text-transform: uppercase;
        opacity: 0.8;
      }
      /* BL-290 — the rack goes TWO-UP instead of a single column.
         The worst case here is not MAX_PAYLOADS(3) but **four** chips: three
         armed payloads plus the Time-Warp chip appended below them. Stacked at
         the touch floor that is ~209 design px of the right rail's height for a
         state the player is in for seconds — while the seven deploy bays, which
         are on screen the entire run, were left at 24.5 REAL css px, well under
         the 44 px floor this project mandates.
         Two columns turn that four-chip worst case into two rows (~153 dp) and
         hand the difference to the bays. It also makes the rack reflow-free in
         the only direction that matters: going from one armed payload to four
         now adds ONE row, not three, so the bays above never jump mid-wave. */
      .payload-chips {
        display: grid;
        grid-template-columns: 1fr 1fr;
        gap: ${UI_SPACE.hair}px;
        align-content: start;
      }
      /* Payload chip on the lit-CRT material: a recessed lit slab with an
         emissive rarity edge + an additive bloom that lifts on hover/armed. */
      /* BL-290 — two-up cells are narrow, and real payload names are long
         UNBREAKABLE tokens (measured worst case: FIREWALL_VERSTÄRKUNG, 20
         chars, no spaces). Beside a state label they shredded mid-word. The
         chip is therefore a small grid: the glyph holds a full-height column,
         and the name gets the cell's ENTIRE remaining width with the state
         under it — so a 20-char token lands in two clean lines. */
      .payload-chip {
        display: grid;
        grid-template-columns: auto minmax(0, 1fr);
        grid-template-rows: auto auto;
        column-gap: ${UI_SPACE.xs}px;
        row-gap: 1px;
        align-items: center;
        text-align: left;
        /* BL-290 — two-up cells are narrower, so the name wraps instead of
           overflowing, and the chip owns a real slab height rather than
           collapsing to its text. */
        min-height: ${CHIP_MIN_H}px;
        max-height: 64px;
        min-width: 0;
        padding: ${UI_SPACE.xxs}px ${UI_SPACE.xs}px;
        border: 1px solid color-mix(in srgb, var(--chip-color, #FFD700) 70%, transparent);
        border-radius: 3px;
        background: linear-gradient(180deg,
          color-mix(in srgb, var(--chip-color, #FFD700) 12%, ${PANEL.BG_ELEVATED}) 0%,
          ${PANEL.BG} 100%);
        color: var(--chip-color, #FFD700);
        font-family: ${UI_FONT_STACK};
        font-size: ${roleFontSize("micro")};
        cursor: pointer;
        transition: box-shadow ${UI_DURATION.hover}ms ${UI_EASE.settle},
                    transform ${UI_DURATION.hover}ms ${UI_EASE.settle};
        /* BL-189 calm-pass (owner steer "calm the ambient, keep the beats"): the
           always-on payload-idle-glow infinite loop is STILLED → a static lit
           edge. The recessed slab still reads as premium hardware (the rarity
           edge + inset highlight), it just no longer pulses-in-competition with
           the other rail loops. The eye rests; nothing ambient leads here. */
        box-shadow: inset 0 1px 0 color-mix(in srgb, var(--chip-color, #FFD700) 20%, transparent),
                    0 0 6px color-mix(in srgb, var(--chip-color, #FFD700) 40%, transparent);
      }
      .payload-chip:not(:disabled):hover {
        box-shadow: inset 0 1px 0 color-mix(in srgb, var(--chip-color, #FFD700) 30%, transparent),
                    0 0 14px var(--chip-color, #FFD700);
        transform: translateX(1px);
      }
      .payload-chip:disabled {
        cursor: default;
        opacity: 0.55;
        box-shadow: inset 0 1px 0 color-mix(in srgb, var(--chip-color, #FFD700) 12%, transparent),
                    0 0 3px color-mix(in srgb, var(--chip-color, #FFD700) 25%, transparent);
      }
      .payload-chip-glyph {
        font-size: ${withUiScale(T.numeric.size - 2)};
        grid-row: 1 / span 2;
        align-self: center;
      }
      .payload-chip-name {
        flex: 1;
        text-transform: uppercase;
        /* Two-up: let a long localized payload name use the cell's two lines
           rather than pushing the state cell out of the chip. */
        min-width: 0;
        line-height: 1.15;
        overflow-wrap: break-word;
        /* BL-292 — the name tracks the rail it lives in, the same way the bay
           specimen does (BL-290). A narrow 16:9/SE-class rail gives each two-up
           cell ~124 design px, where the longest real DE token
           (FIREWALL_VERSTÄRKUNG) cannot fit two clamped lines at the full micro
           size; a wide ultrawide rail has room to spare. Derived from the rail,
           never from a device check, and floored so it can never fall under the
           functional-text minimum. */
        font-size: clamp(11px, calc(var(--right-panel-width) * 0.042), 14px);
        /* BL-290 — HARD CAP at two lines. Without it a long localized payload
           name (measured worst case: the DE strings in a narrow two-up cell)
           grew the chip to 201.9 design px and pushed the whole rack — and the
           seven bays above it — off the rail. A deploy control must have a
           height its content cannot renegotiate mid-wave. */
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }
      .payload-chip-state {
        font-size: ${roleFontSize("micro")};
        opacity: 0.6;
        white-space: nowrap;
        grid-column: 2;
      }
      /* When targets are live, the state cell becomes a bright call-to-action. */
      .payload-rack--hot .payload-chip-state {
        opacity: 1;
        font-weight: 700;
        letter-spacing: 0.5px;
      }
      /* BL-189 — "targets live" is an EVENT (a deployable state), but it was a
         0.8s INFINITE strobe. The calm-pass keeps the BEAT — the chip lights to
         its bright deploy edge — but holds it (a single transition, not a loop),
         so it reads "armed & ready" without competing pulse. The bright edge +
         the bold "▸ DEPLOY" text carry the urgency. */
      .payload-rack--hot .payload-chip:not(:disabled) {
        box-shadow: inset 0 1px 0 color-mix(in srgb, var(--chip-color, #FFD700) 30%, transparent),
                    0 0 12px var(--chip-color, #FFD700);
      }
      /* BL-193 — 44px touch floor on payload chips (the mid-run deploy controls;
         cf. TowerSelector.ts:795-798).

         BL-291 — this one was not merely cosmetic, it was ACTIVE SHRINKAGE.
         Since BL-290 gave the chip its CHIP_MIN_H slab above, the bare
         44px here — later in the same stylesheet, equal specificity — won on
         pointer:coarse and pulled the floor back down to 44. Measured on the
         852×393 target with the full four-chip worst case: the chip settled at
         its 46.4 design-px content height instead of the 52 dp slab, so the
         TOUCH build got a 5.6 dp SMALLER target than the desktop build, on the
         devices the rule is for. Same inversion modalShell.ts documents.
         max() keeps 44 as a hard floor and takes the larger slab wherever it
         is declared, which is everywhere. */
      @media (pointer:coarse) {
        .payload-chip { min-height: max(44px, ${CHIP_MIN_H}px); }
      }
      /* D-335 — the CR-10 phone-tier ROUND-ORB restyle is RETIRED. It was a
         workaround for the right rail overflowing when "// LOADOUT" still
         shared the column (the owner saw only the yellow header at the very
         bottom, the chips cut off below it) — that pressure is gone since
         D-333 relocated "// LOADOUT" to the left rail. The owner's premium-
         feeling ask ("zu überladen … mit premium feeling") wants the SAME
         labelled slab presentation (payload name + the "DEPLOY" affordance)
         at every tier, matching desktop 1:1 — no phone-tier override left
         below. Proven by measurement (real Chromium, docs/decisions.md D-335)
         that #right-panel still does not scroll with a FULL 3-payload rack
         plus the time-warp chip at both shipped chrome-scale ceilings. */
      /* BL-195 motion-a11y: belt-and-braces — under prefers-reduced-motion any
         chip animation (current or future) is hard-killed and transforms removed.
         The chips stay fully lit + legible; only motion is dropped. */
      @media (prefers-reduced-motion: reduce) {
        .payload-chip,
        .payload-rack--hot .payload-chip {
          animation: none;
          transition: none;
        }
        .payload-chip:not(:disabled):hover { transform: none; }
      }
    `;
    document.head.appendChild(style);
  }
}
